import { Compass, Eye, Heart, Lightbulb, Scale, Sparkles } from "lucide-react";
import { Section, Overline, SectionHeading } from "@/components/site/Section";
import CtaBanner from "@/components/site/CtaBanner";

const ABOUT_IMG =
  "https://images.unsplash.com/photo-1758691737217-77302c5f988f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHw0fHxkaXZlcnNlJTIwYnVzaW5lc3MlMjB0ZWFtJTIwbW9kZXJuJTIwb2ZmaWNlfGVufDB8fHx8MTc3ODc3NjYwNXww&ixlib=rb-4.1.0&q=85";

const values = [
  { icon: Heart, title: "People First", body: "Every placement is a person. We invest in candidate experience as much as we invest in client outcomes." },
  { icon: Scale, title: "Transparency", body: "Clear pricing, honest timelines, and consistent communication — no surprises." },
  { icon: Lightbulb, title: "Curiosity", body: "Markets shift, expectations change. We study the work, the talent, and the trends." },
  { icon: Compass, title: "Partnership", body: "We act as an extension of your team — not as an outside vendor." },
  { icon: Sparkles, title: "Quality over Volume", body: "Curated shortlists. Real diligence. Hires that stay." },
  { icon: Eye, title: "Long-Term View", body: "We measure success in retention and culture fit — not just signed offers." },
];

function AboutHero() {
  return (
    <Section className="pb-12 md:pb-16">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-7">
          <Overline className="mb-5">About X-HRverse</Overline>
          <h1 className="font-display text-5xl md:text-6xl tracking-tight leading-[1.05]">
            Strategic workforce solutions, <span className="italic text-[hsl(var(--primary))]">built for growth.</span>
          </h1>
          <p className="mt-6 text-lg md:text-xl text-muted-foreground max-w-2xl">
            We believe successful businesses are built by exceptional people. Our mission is to
            help organizations attract, hire, and retain top talent through modern recruitment
            strategies and scalable HR solutions.
          </p>
        </div>
        <div className="lg:col-span-5">
          <div className="aspect-[5/4] overflow-hidden rounded-xl border border-border">
            <img src={ABOUT_IMG} alt="Team collaborating" className="w-full h-full object-cover" />
          </div>
        </div>
      </div>
    </Section>
  );
}

function MissionVision() {
  return (
    <Section className="bg-[hsl(var(--secondary))]/40 border-y border-border">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border border border-border rounded-xl overflow-hidden">
        <div className="bg-[hsl(var(--background))] p-8 md:p-12">
          <Overline className="mb-4">Our Mission</Overline>
          <p className="font-display text-2xl md:text-3xl leading-snug">
            Empower businesses with scalable recruitment and HR solutions designed to attract,
            engage, and retain top talent in an evolving workforce landscape.
          </p>
        </div>
        <div className="bg-[hsl(var(--background))] p-8 md:p-12">
          <Overline className="mb-4">Our Vision</Overline>
          <p className="font-display text-2xl md:text-3xl leading-snug">
            To become a trusted global workforce partner helping organizations build stronger
            teams through strategic talent and HR solutions.
          </p>
        </div>
      </div>
    </Section>
  );
}

function Story() {
  return (
    <Section>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <SectionHeading eyebrow="Our Story" title="A relationship-driven approach to workforce solutions." />
        </div>
        <div className="lg:col-span-7 space-y-5 text-muted-foreground leading-relaxed text-base md:text-lg">
          <p>
            X-HRverse partners with startups, growing businesses, and enterprise organizations
            across the United States to deliver workforce solutions that improve hiring outcomes,
            reduce recruitment costs, and support long-term business growth.
          </p>
          <p>
            Founded on the belief that hiring is too important to be transactional, we built a
            firm where dedicated recruiters work as strategic advisors — taking time to
            understand culture, role context, and long-term goals before sourcing a single
            candidate.
          </p>
          <p>
            Today, we serve clients across technology, healthcare, aerospace, engineering,
            finance, legal, and HR — each engagement tailored to the business it supports.
          </p>
        </div>
      </div>
    </Section>
  );
}

function Values() {
  return (
    <Section className="bg-[hsl(var(--secondary))]/40 border-y border-border">
      <SectionHeading eyebrow="Our Values" title="The principles behind every engagement." />
      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border rounded-xl overflow-hidden">
        {values.map((v) => (
          <div key={v.title} className="bg-[hsl(var(--background))] p-7 md:p-8 hover:bg-[hsl(var(--secondary))]/40 transition-colors">
            <v.icon className="h-6 w-6 text-[hsl(var(--accent))]" />
            <h3 className="mt-5 font-display text-xl">{v.title}</h3>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{v.body}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}

export default function About() {
  return (
    <div data-testid="page-about">
      <AboutHero />
      <MissionVision />
      <Story />
      <Values />
      <CtaBanner
        title="Want to learn how we approach your industry?"
        description="Schedule a no-obligation conversation with our team."
        ctaLabel="Schedule a Consultation"
        testid="about-cta"
      />
    </div>
  );
}
